/**
 * 网页版的全局状态（对应 iOS AppModel）：登录阶段、当前孩子、全局提示、确认对话框。
 * 启动：hydrate 本地快照 → GET /auth/status → 未登录显示登录页；
 *       已登录 → startSync → GET family → GET devices + family/presence。
 * 网页版没有生物验证：删除等敏感操作用 confirm() 二次确认。
 */
import { useMemo } from "react";
import { api, ApiError, CSRF_HEADER, fetchAuthStatus, onUnauthorized } from "./api";
import { LocalCalendar } from "./calendar";
import type { Child, Device, Family, FamilyMember, Parent } from "./models";
import { createStore, dataStore, hydrate, select, setDevices, setFamilyInfo, setOnlineDeviceIds, shallowEqual, useStore, useStoreOf, wipe } from "./store";
import { onRealtime } from "./realtime";
import { startSync, stopSync, syncNow } from "./sync";

export type Phase = "loading" | "login" | "ready" | "error";

export interface Toast {
  id: number;
  text: string;
  error: boolean;
}

export interface ConfirmRequest {
  id: number;
  title: string;
  message?: string;
  confirmText: string;
  cancelText: string;
  /** 危险操作：确认按钮用红色 */
  danger: boolean;
  resolve: (ok: boolean) => void;
}

export interface SessionState {
  phase: Phase;
  /** 启动失败（网络等）时的说明 */
  error: string | null;
  /** 登录页提示（例如"登录已过期"） */
  loginMessage: string | null;
  /** 后端开了演示账号密码登录（本地开发） */
  devLogin: boolean;
  selectedChildId: string | null;
  toast: Toast | null;
  confirm: ConfirmRequest | null;
}

const CHILD_KEY = "kp.web.selected_child";

function readSelectedChild(): string | null {
  try {
    return localStorage.getItem(CHILD_KEY);
  } catch {
    return null;
  }
}

export const sessionStore = createStore<SessionState>({
  phase: "loading",
  error: null,
  loginMessage: null,
  devLogin: false,
  selectedChildId: readSelectedChild(),
  toast: null,
  confirm: null,
});

export function useSession<T>(selector: (s: SessionState) => T, isEqual?: (a: T, b: T) => boolean): T {
  return useStoreOf(sessionStore, selector, isEqual);
}

// ---------- 启动与登录 ----------

let booted = false;
let realtimeUnsubs: (() => void)[] = [];

onUnauthorized(() => {
  if (sessionStore.getState().phase !== "ready") return;
  stopSession();
  sessionStore.setState({ phase: "login", loginMessage: "登录已过期，请重新登录" });
});

/** 页面启动时调用一次 */
export async function boot(): Promise<void> {
  if (booted) return;
  booted = true;
  await hydrate();
  await checkStatus();
}

/** 查询登录状态并进入对应阶段（启动失败页的"重试"也用它） */
export async function checkStatus(): Promise<void> {
  sessionStore.setState({ phase: "loading", error: null });
  try {
    const st = await fetchAuthStatus();
    sessionStore.setState({ devLogin: st.devLogin });
    if (!st.loggedIn) {
      sessionStore.setState({ phase: "login" });
      return;
    }
    enterReady();
  } catch (e) {
    // 没网但本地有数据：先进入并显示本地数据，同步会自动重试
    if (dataStore.getState().family) {
      enterReady();
      toast("网络连接失败，正在显示本地数据", { error: true });
      return;
    }
    sessionStore.setState({ phase: "error", error: e instanceof Error ? e.message : String(e) });
  }
}

function enterReady(): void {
  sessionStore.setState({ phase: "ready", loginMessage: null });
  void loadSession();
}

async function loadSession(): Promise<void> {
  // 先确认家庭（换了家庭会清空本地快照），再开始同步
  await refreshFamily();
  startSync();
  realtimeUnsubs.push(onRealtime("device_revoked", () => void refreshDevices()));
  await syncNow();
  ensureSelectedChild();
  await refreshDevices();
}

function stopSession(): void {
  stopSync();
  for (const u of realtimeUnsubs) u();
  realtimeUnsubs = [];
}

/** Home4i 登录：整页跳到 Worker 的 /auth/login，登录完成后回到 next */
export function loginWithHome4i(next = location.pathname + location.search): void {
  location.assign(`/auth/login?next=${encodeURIComponent(next)}`);
}

/** 本地开发：演示账号密码登录（Worker 的 DEV_PASSWORD_LOGIN=enabled 时可用） */
export async function devLogin(email: string, password: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch("/auth/dev-login", {
      method: "POST",
      headers: { "Content-Type": "application/json", [CSRF_HEADER]: "1" },
      body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
    });
  } catch {
    throw new ApiError(0, "network", "网络连接失败，请检查网络");
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
    throw new ApiError(res.status, body.error?.code ?? `http_${res.status}`, body.error?.message ?? `登录失败（${res.status}）`);
  }
  enterReady();
}

export async function logout(): Promise<void> {
  try {
    await fetch("/auth/logout", { method: "POST", headers: { [CSRF_HEADER]: "1" } });
  } catch {
    // 网络失败也照样清掉本地数据
  }
  stopSession();
  await wipe();
  setSelectedChild(null);
  sessionStore.setState({ phase: "login", loginMessage: null });
}

// ---------- 家庭与设备 ----------

export async function refreshFamily(): Promise<void> {
  try {
    const r = await api.get<{ family: Family; me: Parent; members: FamilyMember[] }>("family");
    const old = dataStore.getState().family;
    // 换了家庭（换账号登录）：清空本地数据重新全量同步
    if (old && old.id !== r.family.id) await wipe();
    setFamilyInfo(r);
  } catch (e) {
    handleError(e);
  }
}

export async function refreshDevices(): Promise<void> {
  try {
    const r = await api.get<{ devices: Device[] }>("devices");
    setDevices(r.devices);
    const online = new Set(dataStore.getState().onlineDeviceIds);
    for (const d of r.devices) if (d.online) online.add(d.id);
    try {
      const p = await api.get<{ onlineDeviceIds: string[] }>("family/presence");
      for (const id of p.onlineDeviceIds) online.add(id);
    } catch {
      // presence 失败不影响设备列表
    }
    setOnlineDeviceIds([...online]);
  } catch (e) {
    if (!(e instanceof ApiError && e.isUnauthorized)) console.warn("[session] 获取设备失败", e);
  }
}

// ---------- 当前孩子 ----------

export function setSelectedChild(id: string | null): void {
  try {
    if (id) localStorage.setItem(CHILD_KEY, id);
    else localStorage.removeItem(CHILD_KEY);
  } catch {
    // 隐私模式等
  }
  sessionStore.setState({ selectedChildId: id });
}

function ensureSelectedChild(): void {
  const id = sessionStore.getState().selectedChildId;
  const s = dataStore.getState();
  if (!id || !s.children[id]) setSelectedChild(select.sortedChildren(s)[0]?.id ?? null);
}

/** 当前孩子：选中的孩子已不存在时取第一个 */
export function currentChildOf(childrenById: Record<string, Child>, selectedId: string | null): Child | null {
  if (selectedId && childrenById[selectedId]) return childrenById[selectedId];
  const list = Object.values(childrenById).sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));
  return list[0] ?? null;
}

export function useChildren(): Child[] {
  return useStore(select.sortedChildren, shallowEqual);
}

export function useCurrentChild(): Child | null {
  const children = useStore((s) => s.children);
  const selected = useSession((s) => s.selectedChildId);
  return useMemo(() => currentChildOf(children, selected), [children, selected]);
}

/** 按家庭时区的日历工具 */
export function useCalendar(): LocalCalendar {
  const tz = useStore((s) => s.family?.timezone ?? null);
  return useMemo(() => new LocalCalendar(tz), [tz]);
}

export function calendarNow(): LocalCalendar {
  return new LocalCalendar(dataStore.getState().family?.timezone);
}

/** 当前家长在家庭里的角色（owner / member） */
export function useMyRole(): string | null {
  return useStore((s) => s.members.find((m) => m.parentId === s.me?.id)?.role ?? null);
}

// ---------- 设备在线状态 ----------

/** 平板状态：实时通道连着 = 在线；没连着但 24 小时内同步过 = 待机；否则离线 */
export type PresenceLevel = "online" | "standby" | "offline";
const PRESENCE_RANK: Record<PresenceLevel, number> = { offline: 0, standby: 1, online: 2 };

export function presenceOf(d: Device, onlineIds: readonly string[], now = Date.now()): PresenceLevel {
  if (onlineIds.includes(d.id)) return "online";
  if (d.lastSeenAt != null && now - d.lastSeenAt < 24 * 3600_000) return "standby";
  return "offline";
}

/** 一个孩子绑定了多台平板时取最好的状态；没有绑定返回 null */
export function bestPresence(devices: Device[], onlineIds: readonly string[]): PresenceLevel | null {
  let best: PresenceLevel | null = null;
  for (const d of devices) {
    const p = presenceOf(d, onlineIds);
    if (best == null || PRESENCE_RANK[p] > PRESENCE_RANK[best]) best = p;
  }
  return best;
}

// ---------- 提示与确认 ----------

let toastSeq = 0;
let toastTimer: ReturnType<typeof setTimeout> | null = null;

/** 全局轻提示，2.5 秒后消失（错误 4 秒） */
export function toast(text: string, opts: { error?: boolean } = {}): void {
  const t: Toast = { id: ++toastSeq, text, error: !!opts.error };
  sessionStore.setState({ toast: t });
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    if (sessionStore.getState().toast?.id === t.id) sessionStore.setState({ toast: null });
  }, t.error ? 4000 : 2500);
}

export function dismissToast(): void {
  sessionStore.setState({ toast: null });
}

let confirmSeq = 0;

/**
 * 确认对话框（替代 iOS 的生物验证二次确认），返回用户是否确认：
 *   if (!(await confirm({ title: "删除这个日程？", danger: true }))) return;
 */
export function confirm(opts: { title: string; message?: string; confirmText?: string; cancelText?: string; danger?: boolean }): Promise<boolean> {
  return new Promise((resolve) => {
    sessionStore.getState().confirm?.resolve(false);
    sessionStore.setState({
      confirm: {
        id: ++confirmSeq,
        title: opts.title,
        message: opts.message,
        confirmText: opts.confirmText ?? "确定",
        cancelText: opts.cancelText ?? "取消",
        danger: !!opts.danger,
        resolve,
      },
    });
  });
}

/** 确认对话框的按钮回调（ui/ConfirmDialog 调用） */
export function answerConfirm(ok: boolean): void {
  const c = sessionStore.getState().confirm;
  if (!c) return;
  sessionStore.setState({ confirm: null });
  c.resolve(ok);
}

/** 错误提示：登录过期由 api.ts 统一切回登录页，这里不再弹提示 */
export function handleError(e: unknown): void {
  if (e instanceof ApiError && e.isUnauthorized) return;
  if ((e as Error)?.name === "AbortError") return;
  toast(e instanceof Error ? e.message : String(e), { error: true });
}

/**
 * 执行一个操作，失败时弹出错误提示并返回 undefined；成功时可选弹出 success 提示：
 *   const ev = await run(() => writeEntity(...), "已保存")
 */
export async function run<T>(op: () => Promise<T>, success?: string): Promise<T | undefined> {
  try {
    const r = await op();
    if (success) toast(success);
    return r;
  } catch (e) {
    handleError(e);
    return undefined;
  }
}
